import React, { useEffect, useState } from "react";
import "./thankyou.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchReport } from "../../functions/fetchReport";
import { setBackButtonUrl } from "../uiSlice";

/** e.g. "5 minutes and 30 seconds" */
function formatDurationLabel(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  if (m === 0) return `${sec} second${sec === 1 ? "" : "s"}`;
  if (sec === 0) return `${m} minute${m === 1 ? "" : "s"}`;
  return `${m} minute${m === 1 ? "" : "s"} and ${sec} second${sec === 1 ? "" : "s"}`;
}

function parseHmsToSeconds(str) {
  if (str == null || typeof str !== "string") return null;
  const parts = str
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length >= 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  return parts[0] * 60 + parts[1];
}

function initialCompletionTimeLabel(state) {
  if (state?.elapsedSeconds != null && state.elapsedSeconds !== "") {
    const s = Number(state.elapsedSeconds);
    if (Number.isFinite(s) && s >= 0) return formatDurationLabel(s);
  }
  const hms = state?.completionTimeHms;
  if (hms != null && hms !== "") {
    const sec = parseHmsToSeconds(String(hms));
    if (sec != null && sec >= 0) return formatDurationLabel(sec);
  }
  return "";
}

/** Elapsed play time in seconds for summary line (completed puzzle). */
function completionElapsedSecondsFromState(state) {
  if (state?.elapsedSeconds != null && state.elapsedSeconds !== "") {
    const s = Number(state.elapsedSeconds);
    if (Number.isFinite(s) && s >= 0) return s;
  }
  const hms = state?.completionTimeHms;
  if (hms != null && hms !== "") {
    const sec = parseHmsToSeconds(String(hms));
    if (sec != null && sec >= 0) return sec;
  }
  return 0;
}

function splitMinutesSeconds(totalSeconds) {
  const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  return {
    minutes: Math.floor(s / 60),
    seconds: s % 60,
  };
}

function ThankYou() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const isFromMobileApp =
    String(sessionStorage.getItem("fromMobileApp") || "").toLowerCase() === "true";
  const initialPoints = (() => {
    const p = location.state?.points;
    if (p === undefined || p === null || p === "") return 0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  })();
  const [points, setPoints] = useState(initialPoints);
  const [completionTimeLabel, setCompletionTimeLabel] = useState(() =>
    initialCompletionTimeLabel(location.state)
  );

  const puzzleOutcome = location.state?.puzzleOutcome;
  const piecesPlaced = location.state?.piecesPlaced;

  useEffect(() => {
    dispatch(setBackButtonUrl("/game"));
  }, [dispatch]);

  useEffect(() => {
    if (!isFromMobileApp) return;
    const timerId = setTimeout(() => {
      // console.log("navigating back");
      window.GameStatus?.postMessage?.("navigate_back");
    }, 5000);

    return () => clearTimeout(timerId);
  }, [isFromMobileApp]);

  useEffect(() => {
    if (!user || !themeData) return;
    fetchReport({
      userId: user.userId,
      token: user?.token || adminToken,
      sessionId: user.sessionId,
      organizationId: user.organizationId,
    })
      .then((data) => {
        if (!data?.report) return;
        const rawPts = data.report.points ?? data.report.score;
        const n = Number(rawPts);
        setPoints(Number.isFinite(n) ? n : 0);
        const t = data.report.time ?? data.report.time1;
        const sec = typeof t === "string" ? parseHmsToSeconds(t) : null;
        if (sec != null) {
          setCompletionTimeLabel(formatDurationLabel(sec));
        }
      })
      .catch(console.error);
  }, [user, adminToken, themeData]);

  const isJigsawOutcome =
    puzzleOutcome === "completed" || puzzleOutcome === "incomplete";

  const scoreLine = completionTimeLabel
    ? `Your score is ${points} points. Time taken: ${completionTimeLabel}.`
    : `Your score is ${points} points.`;

  const { minutes: doneMinutes, seconds: doneSeconds } = splitMinutesSeconds(
    completionElapsedSecondsFromState(location.state)
  );

  return (
    <>
      <div className="thank-you-container">
        <img src="/thankyou.gif" className="thank-you-gif" alt="" />
        {isJigsawOutcome ? (
          puzzleOutcome === "completed" ? (
            <div className="thank-you-outcome thank-you-outcome--puzzle">
              <h2 className="thank-you-outcome-title">🧩 Puzzle Complete!</h2>
              <p className="thank-you-outcome-body">
                You jig-sawed it in {doneMinutes} min {doneSeconds} sec and scored{" "}
                {points} points.
              </p>
            </div>
          ) : (
            <div className="thank-you-outcome thank-you-outcome--puzzle">
              <h2 className="thank-you-outcome-title">⌛ Time's Up!</h2>
              <p className="thank-you-outcome-body">
                {typeof piecesPlaced === "number" && Number.isFinite(piecesPlaced) ? (
                  <>
                    So close! You placed {piecesPlaced} piece
                    {piecesPlaced === 1 ? "" : "s"} before the clock ran out.
                  </>
                ) : (
                  <>So close! The clock ran out before you finished the puzzle.</>
                )}
              </p>
              <p className="thank-you-outcome-tip">
                Tip: Start with corners and edges.
              </p>
            </div>
          )
        ) : (
          <div className="thank-you-text">{scoreLine}</div>
        )}
      </div>
    </>
  );
}

export default ThankYou;
